import { useRef } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { Play, Video } from "lucide-react";

import { AlertNote } from "../../../components/AlertNote";
import { brand } from "../../../theme/theme";
import { formatClock } from "./platformMeta";

/** Duration and size of a local video (object URL); nulls when the browser can't read it. */
export const readVideoMetadata = (url) =>
  new Promise((resolve) => {
    const video = document.createElement("video");
    const done = (meta) => {
      video.removeAttribute("src");
      video.load();
      resolve(meta);
    };
    video.preload = "metadata";
    video.muted = true;
    video.onloadedmetadata = () =>
      done({
        duration: Number.isFinite(video.duration) ? video.duration : null,
        width: video.videoWidth || null,
        height: video.videoHeight || null,
      });
    video.onerror = () => done({ duration: null, width: null, height: null });
    video.src = url;
  });

/** "0:45" on a dark pill, bottom left of a video. */
export function DurationBadge({ seconds, sx = {} }) {
  if (seconds == null) return null;
  return (
    <Box
      component="span"
      sx={{
        position: "absolute",
        left: 8,
        bottom: 8,
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 0.75,
        py: "1px",
        borderRadius: "6px",
        backgroundColor: "rgba(15, 23, 42, 0.72)",
        color: "#fff",
        fontSize: "0.6875rem",
        fontWeight: 700,
        fontVariantNumeric: "tabular-nums",
        pointerEvents: "none",
        ...sx,
      }}
    >
      <Video size={11} aria-hidden /> {formatClock(seconds)}
    </Box>
  );
}

/** Round play icon in the middle of a thumbnail. */
export function PlayOverlay({ size = 44 }) {
  return (
    <Box
      component="span"
      aria-hidden
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: size,
        height: size,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(15, 23, 42, 0.6)",
        color: "#fff",
        boxShadow: "0 2px 8px rgba(15, 23, 42, 0.25)",
        pointerEvents: "none",
      }}
    >
      <Play size={Math.round(size * 0.42)} fill="#fff" style={{ marginLeft: 2 }} />
    </Box>
  );
}

/**
 * Still of a video filling its parent: its thumbnail, or a dark placeholder with a video
 * icon when bundle.social gave none. Play icon on top.
 */
export function VideoPoster({ src, showPlay = true, playSize = 44 }) {
  return (
    <>
      {src ? (
        <Box
          component="img"
          src={src}
          alt=""
          loading="lazy"
          sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      ) : (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.75,
            background: "linear-gradient(160deg, #1F2937 0%, #111827 100%)",
            color: "rgba(255,255,255,0.7)",
          }}
        >
          {!showPlay && <Video size={22} aria-hidden />}
        </Box>
      )}
      {showPlay && <PlayOverlay size={playSize} />}
    </>
  );
}

/**
 * Segmented control with the publication formats of a network (radio group:
 * arrow keys move between options).
 */
export function FormatSelector({ options, value, onChange, disabled = false, busyValue = null, networkLabel = "" }) {
  const refs = useRef([]);
  const index = Math.max(0, options.findIndex((option) => option.value === value));

  const focusAndPick = (nextIndex) => {
    const next = (nextIndex + options.length) % options.length;
    refs.current[next]?.focus();
    onChange(options[next].value);
  };

  return (
    <Box
      role="radiogroup"
      aria-label={`Formato de publicación${networkLabel ? ` en ${networkLabel}` : ""}`}
      sx={{
        display: "inline-flex",
        p: "3px",
        gap: "2px",
        borderRadius: "10px",
        backgroundColor: "#F1F3F6",
        maxWidth: "100%",
      }}
    >
      {options.map((option, optionIndex) => {
        const active = option.value === value;
        return (
          <Box
            key={option.value}
            ref={(node) => {
              refs.current[optionIndex] = node;
            }}
            component="button"
            type="button"
            role="radio"
            aria-checked={active}
            aria-busy={busyValue === option.value || undefined}
            tabIndex={optionIndex === index ? 0 : -1}
            title={option.hint}
            disabled={disabled}
            onClick={() => !active && onChange(option.value)}
            onKeyDown={(event) => {
              if (event.key === "ArrowRight" || event.key === "ArrowDown") {
                event.preventDefault();
                focusAndPick(optionIndex + 1);
              } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
                event.preventDefault();
                focusAndPick(optionIndex - 1);
              }
            }}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1.25,
              py: 0.5,
              border: "none",
              borderRadius: "8px",
              font: "inherit",
              fontSize: "0.8125rem",
              fontWeight: 600,
              whiteSpace: "nowrap",
              color: active ? "#111827" : "#6B7280",
              backgroundColor: active ? "#fff" : "transparent",
              boxShadow: active ? "0 1px 2px rgba(15, 23, 42, 0.12)" : "none",
              cursor: disabled ? "default" : active ? "default" : "pointer",
              transition: "background-color 0.15s, color 0.15s",
              "&:hover": !active && !disabled ? { color: "#111827" } : {},
              "&:focus-visible": { outline: `2px solid ${brand.pink}`, outlineOffset: 1 },
            }}
          >
            {busyValue === option.value && <CircularProgress size={11} sx={{ color: "inherit" }} />}
            {option.label}
          </Box>
        );
      })}
    </Box>
  );
}

/** Non-blocking warnings of the media against the chosen format. */
export function MediaWarnings({ warnings }) {
  if (!warnings?.length) return null;
  return (
    <AlertNote variant="warning" sx={{ p: 1.5 }}>
      {warnings.length === 1 ? (
        warnings[0]
      ) : (
        <Box component="ul" sx={{ m: 0, pl: 2 }}>
          {warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </Box>
      )}
      <Typography component="div" sx={{ mt: 0.5, fontSize: "0.75rem", color: "#6B7280" }}>
        Puedes seguir editando, pero no se podrá programar hasta corregirlo.
      </Typography>
    </AlertNote>
  );
}
